import React from 'react';
import PropTypes from 'prop-types';
import ReactDOM from 'react-dom';
import {IntlProvider} from 'react-intl';
import Loadable from 'react-loadable';

import Spinner from '@stubhub/react-spinner';

import CheckoutButton from './components/mapbox-checkout-button';
import Dropdown from './components/react-dropdown-list/index.js';
import {
  seatProps,
  rowProps,
  getListingsFromSectionRowIds,
  venueConfigId,
  getsCoordsOfAGroupOfSeats,
  getMiddleOfCoordsArray,
} from './helper-functions/mapbox_helpers';
import SeatMapClickHandlers from './seatMapClickHandlers.js';

class MapboxVoidSSR {}
const mapboxgl = __CLIENT__ ? require('mapbox-gl') : {Popup: MapboxVoidSSR};

const RowHoverTooltip = Loadable({
  loader: () => import(/* webpackChunkName: "event-seat-map" */ './components/tooltips'),
  render(loaded, props) {
    const TooltipComponent = loaded.RowHoverTooltip;

    return <TooltipComponent {...props} />;
  },
  loading: Spinner,
});

const DesktopClickTooltip = Loadable({
  loader: () => import(/* webpackChunkName: "event-seat-map" */ './components/tooltips'),
  render(loaded, props) {
    const TooltipComponent = loaded.DesktopClickTooltip;

    return <TooltipComponent {...props} />;
  },
  loading: Spinner,
});

const MobileClickTooltip = Loadable({
  loader: () => import(/* webpackChunkName: "event-seat-map" */ './components/tooltips'),
  render(loaded, props) {
    const TooltipComponent = loaded.MobileClickTooltip;

    return <TooltipComponent {...props} />;
  },
  loading: Spinner,
});

export default class SeatMapToolTip extends React.Component {
  generatePopup(hasCloseButton, isCloseOnClick, offset = 16, maxWidth, anchorPos, className) {
    return new mapboxgl.Popup({
      closeButton: hasCloseButton,
      closeOnClick: isCloseOnClick,
      maxWidth,
      anchor: anchorPos,
      className,
      offset,
    });
  }

  onCloseTooltip = (e, tooltip) => {
    // TODO: change this name, also wrap it in protective code
    const {onCloseTooltip, device, setListingSelected, mapConfigData, mapInstance, setStateFromObject} = this.props;
    if (onCloseTooltip) {
      onCloseTooltip(e);
    }
    tooltip.remove();
    if (device === 'phone') {
      setListingSelected(null);
      setStateFromObject({usidsOfListingSelected: null});
      if (mapInstance.getZoom() > mapConfigData.ZOOM_TOOLTIP_BREAKPOINT) {
        mapInstance.zoomTo(mapConfigData.ZOOM_TOOLTIP_BREAKPOINT, {duration: 1000});
      }
    }
  };

  setToolTipPosition = (coordinates, feature, uniqueSeatIdArray) => {
    const {actions, seatMapActions} = this.props;
    if (feature && feature.geometry && feature.geometry.type && feature.geometry.coordinates) {
      if (feature.geometry.type === 'Point') {
        const coordsOfAGroupOfSeats = getsCoordsOfAGroupOfSeats(this.seatCoordCache, uniqueSeatIdArray);
        const position =
          coordsOfAGroupOfSeats.length !== 0
            ? getMiddleOfCoordsArray(coordsOfAGroupOfSeats)
            : feature.geometry.coordinates;

        if (position) {
          seatMapActions.clickTooltip.setLngLat(position);
        }
      } else if (feature.geometry.type === 'LineString') {
        const position = actions.rowCenter(feature);
        if (position) {
          seatMapActions.clickTooltip.setLngLat(position);
        }
      } else if (feature.geometry.type === 'Polygon') {
        const position = actions.polygonSectionCenter(feature);
        if (position) {
          seatMapActions.clickTooltip.setLngLat(position);
        }
      } else if (feature.geometry.type === 'MultiPolygon') {
        const position = actions.multipolygonSectionCenter(feature);
        if (position) {
          seatMapActions.clickTooltip.setLngLat(position);
        }
      }
    }
  };

  setupTooltipCheckoutButton = (props) => {
    const {listingId, price} = seatProps(props);
    const {eventId, byoQuantity} = props;
    const {currency} = price;
    const checkoutParamsString = `currency=${currency}&event_id=${eventId}&ticket_id=${listingId}&quantity_selected=${byoQuantity}${this.guestCheckout}`;

    return (
      <CheckoutButton
        className="MapboxClickTooltipDesktop__checkoutButton"
        checkoutText="Checkout"
        onClick={this.handleCheckout}
        checkoutParamsString={checkoutParamsString}
      />
    );
  };

  setupTooltipDropdown = (props) => {
    const {splitVector} = seatProps(props);
    const {byoQuantity} = props;

    let dropdownBtn = null;

    if (splitVector.length > 0) {
      const disabled = splitVector.length === 1;
      dropdownBtn = (
        <Dropdown
          onSelection={this.handleQuantitySelection}
          disabled={disabled}
          byoQuantity={byoQuantity}
          splitVector={splitVector}
        />
      );
    }

    return dropdownBtn;
  };

  setupTooltipComponent = (props) => {
    const {price, sectionName, seatsStr, row} = seatProps(props);
    const {device, type, listingSelected, venueConfig, seatMapActions} = props;
    const {sectionId} = listingSelected;
    const {venueId} = venueConfig;

    if (device === 'phone') {
      return (
        <MobileClickTooltip
          section={sectionName}
          row={row}
          seat={seatsStr}
          price={price}
          onClose={(e) => this.onCloseTooltip(e, seatMapActions.clickTooltip)}
          onContinue={(e) => this.handleContinueButtonClick(e)}
          type={type}
          venueId={venueId}
          venueConfigId={venueConfigId(props)}
          sectionId={sectionId}
          className="MapboxClickTooltipMobile"
        />
      );
    } else if (device === 'desktop') {
      return (
        <DesktopClickTooltip
          section={sectionName}
          row={row}
          seat={seatsStr}
          price={price}
          onClose={(e) => this.onCloseTooltip(e, seatMapActions.clickTooltip)}
          checkoutButton={this.setupTooltipCheckoutButton(props)}
          dropdown={this.setupTooltipDropdown(props)}
          type={type}
          className="MapboxClickTooltipDesktop"
        />
      );
    }
  };

  renderToolTip = (props) => {
    const {listingSelected, seatMapActions, mapInstance} = this.props;
    if (listingSelected) {
      if (seatMapActions.clickTooltip.isOpen()) {
        seatMapActions.clickTooltip.remove();
      }

      const div = document.createElement('div');

      ReactDOM.render(<IntlProvider>{this.setupTooltipComponent(props)}</IntlProvider>, div);

      seatMapActions.clickTooltip.setDOMContent(div).addTo(mapInstance);
    }
  };

  renderRowToolTip = (e, urid) => {
    const [sectionId, rowId] = urid.split('-');
    const listings = getListingsFromSectionRowIds(sectionId, rowId, this.props);
    const {row, startingPrice} = rowProps(listings);
    const {mapInstance, type} = this.props;
    // More protective
    if (row && startingPrice) {
      const div = document.createElement('div');
      ReactDOM.render(
        <IntlProvider>
          <RowHoverTooltip row={row} price={startingPrice} type={type} className="MapboxRowHoverTooltip" />
        </IntlProvider>,
        div
      );

      /*
       * NOTE: Current row hover tooltip is placed at a point where the mouse hovered onto a row
       * TODO: It is better to locate row hover tooltip base on row orientation.
       */
      this.rowTooltip.setDOMContent(div).addTo(mapInstance).setLngLat(e.lngLat);
    }
  };
  render() {
    return (
      <SeatMapClickHandlers
        {...this.props}
        renderRowToolTip={this.renderRowToolTip}
        renderToolTip={this.renderToolTip}
        setToolTipPosition={this.setToolTipPosition}
        onCloseTooltip={this.onCloseTooltip}
        generatePopup={this.generatePopup}
      />
    );
  }
}

SeatMapToolTip.propTypes = {
  /** The actions object. */
  actions: PropTypes.shape({
    /** The function to get the center of a multipolygon section. */
    multipolygonSectionCenter: PropTypes.func,

    /** The function to get the center of a polygon section. */
    polygonSectionCenter: PropTypes.func,

    /** The function to get the center of a row. */
    rowCenter: PropTypes.func,
  }),

  /** The device type. */
  device: PropTypes.string,

  /** The selected listing. */
  listingSelected: PropTypes.object,

  /** The map config data. */
  mapConfigData: PropTypes.object,

  /** The map instance. */
  mapInstance: PropTypes.shape({
    /** The function to get the zoom level. */
    getZoom: PropTypes.func,

    /** The function to zoom to a specific level. */
    zoomTo: PropTypes.func,
  }),

  /** The function to close the tooltip. */
  onCloseTooltip: PropTypes.func,

  /** The seat map actions object. */
  seatMapActions: PropTypes.shape({
    /** The click tooltip object. */
    clickTooltip: PropTypes.object,
  }),

  /** The function to set the selected listing. */
  setListingSelected: PropTypes.func,

  /** The function to set the state from an object. */
  setStateFromObject: PropTypes.func,

  /** The type of tooltip. */
  type: PropTypes.string,
};
